import { useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { useCloudStatus } from '../../hooks/use-cloud-status';

import { createJsmIncidentSetupAnalytics } from './create-jsm-incident-setup-analytics';
import {
	INCIDENT_TEMPLATE_ID,
	type JsmIncidentManagementIntent,
	type JsmIncidentManagementSetupState,
} from './jsm-incident-management-setup-state';
import {
	JsmIncidentManagementSetupError,
	runJsmIncidentManagementSetup,
} from './run-jsm-incident-management-setup';

import type { JsmUseCaseProjectStatus } from './use-jsm-use-case-project-create';

type IncidentSetupResult = Omit<JsmIncidentManagementSetupState, 'stage'> & {
	readonly status: JsmIncidentManagementSetupState['stage'];
	readonly failure?: JsmIncidentManagementSetupError;
	readonly scope?: string;
};

type IncidentSetupOptions = {
	readonly intent: JsmIncidentManagementIntent | undefined;
	readonly templateId: string | undefined;
	readonly projectKey: string | undefined;
	readonly projectStatus: JsmUseCaseProjectStatus;
};

export const useJsmIncidentManagementSetup = ({
	intent,
	templateId,
	projectKey,
	projectStatus,
}: IncidentSetupOptions): IncidentSetupResult => {
	const client = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useCloudStatus();
	const { accountId, isUserLoggedIn, requestInProgress } = useLoggedInUser();
	const { signupId, onDemandUrl } = useSearchParameters();
	const isPlayground = isJsmPlaygroundSignup(window.location.search);
	const scope = JSON.stringify([
		intent,
		templateId,
		cloudId,
		projectKey,
		accountId,
		isUserLoggedIn,
		signupId,
		onDemandUrl,
		isPlayground,
	]);
	const started = useRef(false);
	const settledScope = useRef<string>();
	const attemptAnalytics = useRef<ReturnType<typeof createJsmIncidentSetupAnalytics>>();
	const [result, setResult] = useState<IncidentSetupResult>({ status: 'idle' });

	useEffect(() => {
		if (!intent || projectStatus !== 'complete' || !projectKey || !cloudId || requestInProgress)
			return;
		if (settledScope.current === scope) return;
		if (!started.current || !attemptAnalytics.current) {
			attemptAnalytics.current = createJsmIncidentSetupAnalytics(createAnalyticsEvent);
		}
		const analytics = attemptAnalytics.current;
		const update = (value: Omit<IncidentSetupResult, 'scope'>) =>
			setResult((previous) => ({
				...(previous.scope === scope ? previous : {}),
				...value,
				scope,
			}));
		let failureReported = false;
		const fail = (failure: JsmIncidentManagementSetupError) => {
			if (failureReported) return;
			failureReported = true;
			settledScope.current = scope;
			update({ status: failure.reason === 'timeout' ? 'timeout' : 'error', failure });
			analytics.fail(failure);
		};
		if (
			!isUserLoggedIn ||
			intent.accountId !== accountId ||
			intent.signupId !== signupId ||
			intent.siteName !== onDemandUrl ||
			templateId !== INCIDENT_TEMPLATE_ID ||
			isPlayground
		) {
			fail(new JsmIncidentManagementSetupError('scope-mismatch'));
			return;
		}
		const controller = new AbortController();
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		let resolveWait: (() => void) | undefined;
		const deadline = setTimeout(() => {
			controller.abort();
			fail(new JsmIncidentManagementSetupError('timeout'));
			resolveWait?.();
		}, 90_000);
		const wait = (delay: number) =>
			new Promise<void>((resolve) => {
				resolveWait = resolve;
				timer = setTimeout(() => {
					timer = undefined;
					resolveWait = undefined;
					resolve();
				}, delay);
			});
		// Deferring admission lets React's effect setup/cleanup probe finish before a mutation starts.
		void Promise.resolve()
			.then(async () => {
				if (cancelled) return;
				if (started.current) throw new JsmIncidentManagementSetupError('interrupted');
				started.current = true;
				analytics.start();
				await runJsmIncidentManagementSetup({
					client,
					intent,
					cloudId,
					signal: controller.signal,
					isGateEnabled: () => checkFeatureFlagGate(featureFlags.operationsLandFlow),
					wait,
					onState: ({ stage, ...ids }) => {
						if (cancelled || controller.signal.aborted) return;
						analytics.progress({ stage, ...ids });
						if (stage === 'complete') settledScope.current = scope;
						update({ status: stage, ...ids });
					},
				});
			})
			.catch((error: unknown) => {
				if (cancelled) return;
				const failure = controller.signal.aborted
					? new JsmIncidentManagementSetupError('timeout')
					: error instanceof JsmIncidentManagementSetupError
						? error
						: new JsmIncidentManagementSetupError('api');
				fail(failure);
			})
			.finally(() => {
				clearTimeout(deadline);
				if (timer !== undefined) clearTimeout(timer);
				resolveWait?.();
			});
		return () => {
			cancelled = true;
			analytics.cancel();
			controller.abort();
			clearTimeout(deadline);
			if (timer !== undefined) clearTimeout(timer);
			resolveWait?.();
		};
	}, [
		intent,
		templateId,
		projectKey,
		projectStatus,
		client,
		createAnalyticsEvent,
		cloudId,
		accountId,
		isUserLoggedIn,
		requestInProgress,
		signupId,
		onDemandUrl,
		isPlayground,
		scope,
	]);

	if (!intent) return { status: 'idle' };
	return result.scope === scope ? result : { status: 'waiting-for-project' };
};
